import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'documentsList',
    'placementsField',
    'dropzone',
    'dropzoneIdle',
    'dropzoneBusy',
    'pendingRowTemplate',
    'submitButton',
    'message',
  ]
  static values = {
    stagedDocumentsUrl: String,
    attachedDocumentsUrl: { type: String, default: '' },
  }

  connect() {
    this.uploading = false
    this.placementsByDocument = new Map()
    this.completeByDocument = new Map()
    this.expectedDocuments = document.querySelectorAll(
      '[data-controller~="document-placer"]'
    ).length
    this.updateSubmitState()

    this.boundOnPlacementsChanged = (event) => {
      this.onPlacementsChanged(event)
    }
    document.addEventListener(
      'document-placer:placements-changed',
      this.boundOnPlacementsChanged
    )

    if (this.hasDropzoneTarget) {
      this.boundOnDragOver = (event) => this.onDragOver(event)
      this.boundOnDragLeave = (event) => this.onDragLeave(event)
      this.boundOnDrop = (event) => this.onDrop(event)
      this.dropzoneTarget.addEventListener('dragover', this.boundOnDragOver)
      this.dropzoneTarget.addEventListener('dragleave', this.boundOnDragLeave)
      this.dropzoneTarget.addEventListener('drop', this.boundOnDrop)
    }

    this.boundOnWindowDragOver = (event) => this.onWindowDragOver(event)
    this.boundOnWindowDrop = (event) => this.onWindowDrop(event)
    window.addEventListener('dragover', this.boundOnWindowDragOver)
    window.addEventListener('drop', this.boundOnWindowDrop)

    this.boundOnModeChange = (event) => this.onModeChange(event)
    this.element.addEventListener('change', this.boundOnModeChange)
    this.broadcastMode()
  }

  disconnect() {
    document.removeEventListener(
      'document-placer:placements-changed',
      this.boundOnPlacementsChanged
    )

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.removeEventListener(
        'dragover',
        this.boundOnDragOver
      )
      this.dropzoneTarget.removeEventListener(
        'dragleave',
        this.boundOnDragLeave
      )
      this.dropzoneTarget.removeEventListener('drop', this.boundOnDrop)
    }

    window.removeEventListener('dragover', this.boundOnWindowDragOver)
    window.removeEventListener('drop', this.boundOnWindowDrop)

    this.element.removeEventListener('change', this.boundOnModeChange)
  }

  onModeChange(event) {
    if (event.target?.name !== 'signature_request[mode]') return
    this.broadcastMode()
  }

  broadcastMode() {
    const checked = this.element.querySelector(
      'input[name="signature_request[mode]"]:checked'
    )
    const mode = checked ? checked.value : 'parallel'
    const dispatched = new CustomEvent(
      'signature-session-form:mode-changed',
      { detail: { mode }, bubbles: true }
    )
    document.dispatchEvent(dispatched)
  }

  onWindowDragOver(event) {
    if (this.containsFiles(event)) event.preventDefault()
  }

  onWindowDrop(event) {
    if (this.containsFiles(event)) event.preventDefault()
  }

  containsFiles(event) {
    const types = Array.from(event.dataTransfer?.types || [])
    return types.includes('Files')
  }

  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    this.dropzoneTarget.classList.add('border-primary', 'bg-primary/5')
  }

  onDragLeave(event) {
    if (event.target === this.dropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-primary', 'bg-primary/5')
    }
  }

  async onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('border-primary', 'bg-primary/5')
    if (this.uploading) return

    const files = Array.from(event.dataTransfer?.files || [])
      .filter((file) => file.type === 'application/pdf')
    if (files.length === 0) return

    await this.handleFiles(files)
  }

  async uploadFiles(event) {
    const input = event.target
    const files = Array.from(input.files || [])
    input.value = ''
    if (this.uploading || files.length === 0) return

    await this.handleFiles(files)
  }

  async handleFiles(files) {
    this.uploading = true
    this.setBusy(true)
    this.clearMessage()

    const pendingRows = files.map((file) => this.appendPendingRow(file))
    let lastRedirect = null
    for (const [index, file] of files.entries()) {
      const pendingRow = pendingRows[index]
      try {
        const document = await this.uploadOne(file, (percent) => {
          this.updatePendingRowProgress(pendingRow, percent)
        })
        this.markPendingRowProcessing(pendingRow)
        const attached = await this.attachToDraft(document.id)
        if (attached?.redirect_url) {
          lastRedirect = attached.redirect_url
        } else {
          this.removePendingRow(pendingRow)
          this.appendDocument(document)
        }
      } catch (error) {
        console.error('Upload failed', error)
        this.removePendingRow(pendingRow)
        this.showError(error.message)
        break
      }
    }

    if (lastRedirect) {
      // Keep the busy state and pending rows visible until the page
      // actually unloads, so slow redirects still show feedback.
      window.location.href = lastRedirect
      return
    }

    pendingRows.forEach((pendingRow) => this.removePendingRow(pendingRow))
    this.uploading = false
    this.setBusy(false)
  }

  setBusy(busy) {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.toggle('pointer-events-none', busy)
      this.dropzoneTarget.classList.toggle('opacity-60', busy)
      this.dropzoneTarget.setAttribute('aria-busy', busy ? 'true' : 'false')
    }
    if (this.hasDropzoneIdleTarget) {
      this.dropzoneIdleTarget.classList.toggle('hidden', busy)
    }
    if (this.hasDropzoneBusyTarget) {
      this.dropzoneBusyTarget.classList.toggle('hidden', !busy)
    }
    if (this.hasSubmitButtonTarget) {
      if (busy) {
        this.submitButtonTarget.disabled = true
      } else {
        this.updateSubmitState()
      }
    }
  }

  appendPendingRow(file) {
    if (!this.hasPendingRowTemplateTarget) return null

    const fragment = this.pendingRowTemplateTarget.content.cloneNode(true)
    const row = fragment.firstElementChild
    const filename = row.querySelector('[data-upload-filename]')
    if (filename) filename.textContent = file.name
    this.documentsListTarget.appendChild(row)
    return row
  }

  updatePendingRowProgress(pendingRow, percent) {
    if (!pendingRow) return

    const bar = pendingRow.querySelector('[data-upload-progress]')
    if (bar) bar.style.width = `${percent}%`
  }

  markPendingRowProcessing(pendingRow) {
    if (!pendingRow) return

    this.updatePendingRowProgress(pendingRow, 100)
    const uploading =
      pendingRow.querySelector('[data-upload-status="uploading"]')
    const processing =
      pendingRow.querySelector('[data-upload-status="processing"]')
    if (uploading) uploading.classList.add('hidden')
    if (processing) processing.classList.remove('hidden')
  }

  removePendingRow(pendingRow) {
    if (pendingRow?.isConnected) pendingRow.remove()
  }

  showError(text) {
    if (!this.hasMessageTarget || !text) return

    this.messageTarget.textContent = text
    this.messageTarget.classList.remove('hidden')
  }

  clearMessage() {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = ''
    this.messageTarget.classList.add('hidden')
  }

  async attachToDraft(documentId) {
    if (!this.hasAttachedDocumentsUrlValue ||
        this.attachedDocumentsUrlValue.length === 0) {
      return null
    }

    const formData = new FormData()
    formData.append('document_id', documentId)

    const response = await fetch(this.attachedDocumentsUrlValue, {
      method: 'POST',
      body: formData,
      headers: { 'X-CSRF-Token': this.csrfToken() },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || 'Attach error')
    }

    return response.json()
  }

  // XMLHttpRequest instead of fetch: it is the only way to observe
  // upload progress (xhr.upload "progress" events).
  uploadOne(file, onProgress) {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('file', file)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', this.stagedDocumentsUrlValue)
      xhr.setRequestHeader('X-CSRF-Token', this.csrfToken())
      xhr.setRequestHeader('Accept', 'application/json')
      xhr.responseType = 'json'

      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable || !onProgress) return
        onProgress(Math.round((event.loaded / event.total) * 100))
      })
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response)
        } else {
          const body = xhr.response || {}
          reject(new Error(body.error || 'Upload error'))
        }
      })
      xhr.addEventListener('error', () => {
        reject(new Error('Upload error'))
      })
      xhr.send(formData)
    })
  }

  appendDocument(documentPayload) {
    const item = window.document.createElement('li')
    item.className = 'flex items-center justify-between text-sm gap-2'
    item.dataset.stagedDocumentId = String(documentPayload.id)

    const label = window.document.createElement('span')
    label.className = 'text-gray-700 truncate'
    label.textContent =
      documentPayload.filename || `Document #${documentPayload.id}`

    const hidden = window.document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = 'signature_request[document_ids][]'
    hidden.value = documentPayload.id

    const removeButton = window.document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'text-xs text-gray-500 hover:text-red-600'
    removeButton.textContent = '×'
    removeButton.addEventListener('click', () => {
      item.remove()
    })

    item.appendChild(label)
    item.appendChild(hidden)
    item.appendChild(removeButton)
    this.documentsListTarget.appendChild(item)
  }

  async removeDocument(event) {
    const documentId = event.currentTarget.dataset.documentId
    if (!documentId || this.attachedDocumentsUrlValue.length === 0) return

    try {
      const response = await fetch(
        `${this.attachedDocumentsUrlValue}/${documentId}`,
        {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': this.csrfToken() },
          credentials: 'same-origin',
        }
      )
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || 'Remove failed')
      }
      const body = await response.json()
      window.location.href = body.redirect_url || window.location.href
    } catch (error) {
      console.error('Remove document failed', error)
    }
  }

  onPlacementsChanged(event) {
    if (!this.hasPlacementsFieldTarget) return

    const { documentId, placements, complete } = event.detail || {}
    if (documentId === undefined) return

    this.placementsByDocument.set(Number(documentId), placements || [])
    this.completeByDocument.set(Number(documentId), Boolean(complete))
    this.syncPlacementsField()
    this.updateSubmitState()
  }

  syncPlacementsField() {
    const all = []
    this.placementsByDocument.forEach((placements) => {
      placements.forEach((placement) => all.push(placement))
    })
    this.placementsFieldTarget.value = JSON.stringify(all)
  }

  // Enable the "next" button only once every document has a zone for each
  // signer. Server-side matrix validation remains the source of truth; this
  // is a progressive-enhancement guard (disabled only when JS is present).
  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return

    const allComplete =
      this.expectedDocuments > 0 &&
      this.completeByDocument.size === this.expectedDocuments &&
      Array.from(this.completeByDocument.values()).every(Boolean)
    this.submitButtonTarget.disabled = !allComplete
  }

  csrfToken() {
    const tag = window.document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.content : ''
  }
}
